import React, { useState, useEffect } from 'react';
import { TextField, IconButton, makeStyles } from '@material-ui/core';
import { Create as EditIcon } from '@material-ui/icons';
import fontFamily from '../../assets/css/fonts';
import color from '../../assets/css/colors';
import QueryParam from '../../services/QueryParamService';
import { getSystemDate } from '../../services/ProfileService';
import {
  listExpiredPositionOption,
  processExpiredPositionOption,
  updateExpiredPosition,
} from '../../services/PositionMngtService';
import PositionMngtModal from './Components/PositionMngtModal';
import Table, { columnType } from '../../components/ClearingTable/Table';
import AutoCompleteCorrespondent from '../../components/AutoComplete/AutoCompleteCorrespondent';
import AutoCompleteAccountNo from '../../components/AutoComplete/AutoCompleteAccountNo';
import AutoCompleteMasterAccountNo from '../../components/AutoComplete/AutoCompleteMasterAccountNo';
import AutoCompleteBranch from '../../components/AutoComplete/AutoCompleteBranch';
import AutoCompleteRep from '../../components/AutoComplete/AutoCompleteRep';
import AutoCompleteSymbol from '../../components/AutoComplete/AutoCompleteSymbol';
import Button from '../../components/Buttons/Button';
import Text from '../../components/Typography/Text';
import SelectSystemCode from '../../components/Dropdown/SelectSystemCode';
import {
  notifyInfo,
  notifyError,
  notifySuccess,
  notifyWarning,
} from '../../components/Messages/Notification';

const useStyles = makeStyles(() => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 800,
    width: '100%',
    margin: '0 auto',
    float: 'clear',
  },
  modalHeader: {
    backgroundColor: 'white',
    padding: 25,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    borderBottom: '1px solid #ccc',
    display: 'flex',
    '& div:nth-child(1)': {
      flex: 1,
    },
    '& div:nth-child(2)': {
      flex: 'none',
      '& .MuiIconButton-root': {
        borderRadius: 5,
        padding: 10,
        '&:hover svg path': {
          fill: color.primary.main,
        },
      },
      '& svg:last-child': {
        width: 15,
        height: 15,
      },
    },
  },
  modalBody: {
    backgroundColor: 'white',
    padding: '0px 25px 25px 25px',
    fontFamily: fontFamily.headers,
    '& p': {
      fontSize: 16,
      lineHeight: '24px',
    },
  },
  modalFooter: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    backgroundColor: 'white',
    padding: '0px 25px 25px 25px',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    position: 'relative',
    '& button': {
      width: 'auto',
    },
  },
  closeButton: {
    '& button': {
      marginTop: '-4px !important',
    },
  },
}));

export default function PositionMngt() {
  const classes = useStyles();

  const columns = [
    {
      name: 'id',
      label: 'ID',
      options: {
        display: false,
      },
    },
    {
      name: 'processDate',
      label: 'Process Date',
      type: columnType.date,
    },
    {
      name: 'systemDate',
      label: 'System Date',
      type: columnType.date,
    },
    {
      name: 'accountId',
      label: 'Account ID',
    },
    {
      name: 'correspondent',
      label: 'Correspondent',
    },
    {
      name: 'accountNo',
      label: 'Account No',
    },
    {
      name: 'symbol',
      label: 'Symbol',
    },
    {
      name: 'underlyingSymbol',
      label: 'Underlying Symbol',
    },
    {
      name: 'strikePrice',
      label: 'Strike Price',
      type: columnType.price,
    },
    {
      name: 'expirationDate',
      label: 'Expiration Date',
      type: columnType.date,
    },
    {
      name: 'pc',
      label: 'PC',
    },
    {
      name: 'secType',
      label: 'Sec Type',
    },
    {
      name: 'closingPrice',
      label: 'Closing Price',
      type: columnType.price,
      addFooter: true,
    },
    {
      name: 'underlyingClosingPrice',
      label: 'Underlying Closing Price',
      type: columnType.price,
      addFooter: true,
    },
    {
      name: 'inTheMoney',
      label: 'In The Money',
      type: columnType.bool,
    },
    {
      name: 'itmOtmAmount',
      label: 'ITM OTM Amount',
      type: columnType.amount,
      addFooter: true,
    },
    {
      name: 'multiplier',
      label: 'Multiplier',
      type: columnType.quantity,
    },
    {
      name: 'side',
      label: 'Side',
    },
    {
      name: 'tdQty',
      label: 'TD Qty',
      type: columnType.quantity,
      addFooter: true,
    },
    {
      name: 'remainingQty',
      label: 'Remaining Qty',
      type: columnType.quantity,
      addFooter: true,
    },
    {
      name: 'eedQty',
      label: 'EED Qty',
      type: columnType.quantity,
      addFooter: true,
    },
    {
      name: 'eedDeclationQty',
      label: 'EED Declation Qty',
      type: columnType.quantity,
      addFooter: true,
    },
    {
      name: 'dnedQty',
      label: 'DNED Qty',
      type: columnType.quantity,
      addFooter: true,
    },
    {
      name: 'dnedDeclationQty',
      label: 'DNED Declation Qty',
      type: columnType.quantity,
      addFooter: true,
    },
    {
      name: 'assignmentQty',
      label: 'Assignment Qty',
      type: columnType.quantity,
      addFooter: true,
    },
    {
      name: 'expirationQty',
      label: 'Expiration Qty',
      type: columnType.quantity,
      addFooter: true,
    },
    {
      name: 'exerciseQty',
      label: 'Exercise Qty',
      type: columnType.quantity,
      addFooter: true,
    },
    {
      name: 'source',
      label: 'Source',
    },
    {
      name: 'status',
      label: 'Status',
    },
  ];

  const options = {
    selectableRows: 'none',
    responsive: 'standard',
    customToolbarSelect: (selectedRows) => {
      return (
        <div className={classes.grdRow} style={{ marginRight: 30, padding: 0 }}>
          <div className={classes.grdCellNone}>
            <Button
              id="batch"
              disabled={loading.search}
              loading={loading.search}
              type="search"
              label={'Process'}
              onClick={() => {
                handleProcess(selectedRows.data.map((d) => d.dataIndex));
              }}
            />
          </div>
        </div>
      );
    },
  };

  const [loading, setLoading] = React.useState(false);
  const [rowsToUpdate] = React.useState([]);
  const [rows, setRows] = useState([]);
  const [rowData, setRowData] = React.useState({});
  const [open, setOpen] = React.useState(false);
  const [searchData, setSearchData] = React.useState(
    QueryParam.get({
      fromDate: '',
      toDate: '',
      correspondent: '',
      accountNo: '',
      symbol: '',
      search: false,
    })
  );

  useEffect(
    () => {
      async function init() {
        const systemDate = await getSystemDate();
        var searchDataCopy = { ...searchData };

        searchDataCopy.fromDate = searchDataCopy.fromDate
          ? searchDataCopy.fromDate
          : systemDate;
        searchDataCopy.toDate = searchDataCopy.toDate
          ? searchDataCopy.toDate
          : systemDate;

        setSearchData(searchDataCopy);
      }

      init();

      if (searchData.search) {
        handleSearch();
      }
    },
    // eslint-disable-next-line
    []
  );

  const handleChange = (e) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;

    setSearchData({
      ...searchData,
      [input.name]: input.value,
    });
  };

  const handleOpen = (data) => {
    if (!data) {
      //ADD default values
      data = {
        expirationDate: '',
        entryType: '',
        qty: '',
      };
    }

    setRowData(data);
    setOpen(true);
  };

  const handleClose = async (data) => {
    if (!data) {
      setOpen(false);
      return;
    }

    try {
      const resp = await updateExpiredPosition(data);
      console.log(data);
      const rowsCopy = [...rows];
      const index = rows.indexOf(rowData);
      rowsCopy[index] = resp.expiredPositions;
      setRows(rowsCopy);
      notifySuccess('Expired Position has been updated.');

      setOpen(false);
    } catch (error) {
      console.error(error.message);
      notifyError(error.message);
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    try {
      let valid = true;
      if (!searchData.fromDate) {
        notifyWarning('From Date is required.');
        valid = false;
      }
      if (!searchData.toDate) {
        notifyWarning('To Date is required.');
        valid = false;
      }

      if (!valid) {
        return;
      }
      setLoading(true);
      QueryParam.set(searchData);
      const data = await listExpiredPositionOption(searchData);
      setRows(data.expiredPositionsList);
      notifyInfo(data.expiredPositionsList.length + ' search results.');
    } catch (error) {
      console.error(error);
      notifyError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async (data) => {
    //PROCESS
    try {
      let expiredPositionsList = [];
      if (data) {
        for (var a = 0; a < data.length; a++) {
          rowsToUpdate[a] = data[a].dataIndex;
        }

        for (a = 0; a < rowsToUpdate.length; a++) {
          expiredPositionsList.push(rows[rowsToUpdate[a]].expiredPositionId);
        }
      }

      await processExpiredPositionOption({
        positions: expiredPositionsList,
      });

      handleSearch();
      notifySuccess('Position(s) has been processed');
    } catch (error) {
      console.error(error);
      notifyError(error.message);
    }
  };

  /*=========================================================================================
  Table component
  ===========================================================================================*/
  return (
    <form className="page-c">
      <div className="search-c">
        <div className="grd-row nm">
          <div className="grd-cell">
            <Text mt={8} variant="h1" label="Position Mngt" />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={loading}
              loading={loading}
              type="search"
              label={loading ? 'Searching...' : 'Search'}
              onClick={handleSearch}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <TextField
              required
              name="fromDate"
              fullWidth
              label="From Date"
              type="date"
              value={searchData.fromDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className="grd-cell">
            <TextField
              required
              name="toDate"
              fullWidth
              label="To Date"
              type="date"
              value={searchData.toDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteCorrespondent
              fullWidth
              name="correspondent"
              value={searchData.correspondent}
              onChange={handleChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteAccountNo
              fullWidth
              name="accountNo"
              value={searchData.accountNo}
              correspondent={searchData.correspondent}
              onChange={handleChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteSymbol
              fullWidth
              name="symbol"
              value={searchData.symbol}
              onChange={handleChange}
            />
          </div>
          <div className="grd-cell"></div>
          <div className="grd-cell"></div>
        </div>
      </div>
      <div className="mt-20">
        <Table title={'Position Mngt'} data={rows} columns={columns} options={options} />
      </div>
      {open && (
        <PositionMngtModal
          onClose={handleClose}
          open={open}
          value={rowData}
          loading={loading.save}
        ></PositionMngtModal>
      )}
    </form>
  );
}
